import type { PasswordRule } from '@didto/domain';
import { decodeJwtPayload } from './jwt';

/**
 * Password recovery (ADR-041, docs/runbooks/password-recovery.md).
 *
 * A password reset email's link signs the login in and lands on the app with `type=recovery` and the
 * session's tokens in the address fragment (or with `error=…` when the link is used up or expired).
 * Supabase Auth gives that session nothing that tells it apart later: its `amr` says `otp`, as an
 * invite's or a magic link's does. So the app notes, from the landing address, which session the
 * link opened (its `session_id`), and only that session gets the reset form.
 *
 * This decides navigation only. Auth decides what the session may do: it applies the password policy
 * to the new password, asks a login with an authenticator for its code first (aal2), and ends the
 * login's other sessions when the password changes.
 */
export const RESET_PASSWORD_PATH = '/reset-password';

/** What Auth put in the address when a link failed (an expired or used link: `otp_expired`). */
export interface LinkError {
  code: string | null;
  description: string | null;
}

export type AuthLanding =
  { kind: 'recovery'; sessionId: string | null } | { kind: 'link-error'; error: LinkError } | null;

/** Reads the address the app was opened with. Tokens come in the fragment; an error may come in the
 *  fragment or the query. */
export function parseAuthLanding(href: string): AuthLanding {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
  const get = (key: string) => fragment.get(key) ?? url.searchParams.get(key);
  const error = get('error');
  const code = get('error_code');
  const description = get('error_description');
  if (error || code || description) return { kind: 'link-error', error: { code, description } };
  const token = fragment.get('access_token');
  if (get('type') === 'recovery' && token)
    return { kind: 'recovery', sessionId: sessionIdOf(token) };
  return null;
}

/** The Auth session a token belongs to (its `session_id` claim). It stays the same when the token
 *  is refreshed or the session is verified with an authenticator. */
export function sessionIdOf(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;
  const id = decodeJwtPayload(accessToken)?.session_id;
  return typeof id === 'string' && id !== '' ? id : null;
}

/* ── which session a reset link opened ── */

/** Where the mark lives: the browser's localStorage, so a reload keeps the reset page. */
export interface MarkStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
const MARK_KEY = 'didto-os-recovery';

function browserStore(): MarkStore | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // storage blocked: the reset still works until the page is reloaded
  }
}

/** Remembered in memory too, for a browser whose storage is blocked. */
let markInMemory: string | null = null;

function writeMark(id: string, store: MarkStore | null): void {
  markInMemory = id;
  try {
    store?.setItem(MARK_KEY, id);
  } catch {
    // the in-memory mark is enough for this page load
  }
}

/** Notes that `accessToken`'s session was opened by a reset link (Auth's PASSWORD_RECOVERY event). */
export function markRecoverySession(
  accessToken: string | null | undefined,
  store: MarkStore | null = browserStore(),
): void {
  const id = sessionIdOf(accessToken);
  if (id) writeMark(id, store);
}

export function forgetRecoverySession(store: MarkStore | null = browserStore()): void {
  markInMemory = null;
  try {
    store?.removeItem(MARK_KEY);
  } catch {
    // nothing stored
  }
}

/** Whether `accessToken` belongs to the session a reset link opened. */
export function isRecoverySession(
  accessToken: string | null | undefined,
  store: MarkStore | null = browserStore(),
): boolean {
  const id = sessionIdOf(accessToken);
  if (!id) return false;
  let stored: string | null;
  try {
    stored = store?.getItem(MARK_KEY) ?? null;
  } catch {
    stored = null; // storage blocked: the in-memory mark decides
  }
  return id === (stored ?? markInMemory);
}

/* ── the address the app was opened with ── */

let landing: AuthLanding = null;

/**
 * Called once, in lib/supabase.ts before the Supabase client exists: the client reads the same
 * address and then clears the tokens from it. Navigation waits for the client (session.tsx), so the
 * client always sees the address as it arrived.
 */
export function captureAuthLanding(
  href: string | null = typeof window !== 'undefined' ? window.location.href : null,
  store: MarkStore | null = browserStore(),
): AuthLanding {
  landing = href ? parseAuthLanding(href) : null;
  if (landing?.kind === 'recovery' && landing.sessionId) writeMark(landing.sessionId, store);
  return landing;
}

/** The error a link landed with, if any. */
export function landingLinkError(): LinkError | null {
  return landing?.kind === 'link-error' ? landing.error : null;
}

/* ── the steps of the reset page ── */

export type RecoveryStep = 'link-error' | 'no-link' | 'verify' | 'form';

/**
 * The page's first step. The session a reset link opened gets the form, after the authenticator code
 * when the login has one (Auth refuses a new password from an aal1 session then). Without that
 * session, a failed link says so; anything else is told the page needs the link.
 */
export function firstRecoveryStep(input: {
  recovery: boolean;
  linkFailed: boolean;
  aal: { currentLevel: string | null; nextLevel: string | null } | null;
}): RecoveryStep {
  if (input.recovery) return needsAuthenticatorCode(input.aal) ? 'verify' : 'form';
  return input.linkFailed ? 'link-error' : 'no-link';
}

/**
 * The page's step once a check has decided `next`: the first decision, and afterwards only towards the
 * reset. The link's session can arrive after the page decided (a second tab of a double-clicked link
 * learns it from the first); nothing replaces the form, the code, the success or an explained failure.
 */
export function settleRecoveryStep<S extends RecoveryStep | 'checking' | 'done'>(
  current: S,
  next: RecoveryStep,
): S | RecoveryStep {
  if (current === 'checking') return next;
  const upgrade =
    (current === 'link-error' || current === 'no-link') && (next === 'form' || next === 'verify');
  return upgrade ? next : current;
}

export function needsAuthenticatorCode(
  aal: { currentLevel: string | null; nextLevel: string | null } | null,
): boolean {
  return aal?.nextLevel === 'aal2' && aal.currentLevel !== 'aal2';
}

/** Why Auth refused `updateUser({ password })`, in the page's terms. */
export type PasswordRefusal =
  | { kind: 'weak'; pwned: boolean; tooLong: boolean }
  | { kind: 'same' }
  | { kind: 'needs-code' }
  | { kind: 'ended' }
  | { kind: 'other' };

export function passwordRefusal(err: {
  name?: string;
  status?: number;
  code?: string;
  message?: string;
  reasons?: readonly string[];
}): PasswordRefusal {
  const code = err.code ?? '';
  if (code === 'weak_password' || err.name === 'AuthWeakPasswordError')
    return { kind: 'weak', pwned: (err.reasons ?? []).includes('pwned'), tooLong: false };
  // more than 72 bytes is a validation failure, not a weak password
  if (code === 'validation_failed' && /longer than/i.test(err.message ?? ''))
    return { kind: 'weak', pwned: false, tooLong: true };
  if (code === 'same_password') return { kind: 'same' };
  if (code === 'insufficient_aal') return { kind: 'needs-code' };
  if (
    err.name === 'AuthSessionMissingError' ||
    [
      'session_not_found',
      'session_expired',
      'refresh_token_not_found',
      'bad_jwt',
      'user_not_found',
    ].includes(code) ||
    err.status === 401 ||
    err.status === 403
  )
    return { kind: 'ended' };
  return { kind: 'other' };
}

/**
 * The rules to name when Auth calls a password weak: the ones this mirror finds broken. `null` when
 * the mirror finds none (Auth's settings are stricter than the mirror): the page shows Auth's words.
 */
export function rulesToName(
  problems: readonly PasswordRule[],
  tooLong: boolean,
): PasswordRule[] | null {
  if (tooLong) return ['max'];
  return problems.length > 0 ? [...problems] : null;
}
